import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import {
  Plus, Trash2, Edit, Upload, X, Package, Palette, Save,
  Image as ImageIcon, Tag, Eye, EyeOff, ChevronDown, ChevronUp, Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'pens', label: 'Pens / Devices' },
  { value: 'cartridges', label: 'Cartridges' },
  { value: 'accessories', label: 'Accessories' },
  { value: 'supplies', label: 'Supplies' },
];

const DEFAULT_COLORS = [
  { name: 'Black', hex: '#000000' },
  { name: 'Blue', hex: '#2563EB' },
  { name: 'Pink', hex: '#EC4899' },
  { name: 'White', hex: '#F8FAFC' },
  { name: 'Red', hex: '#DC2626' },
  { name: 'Green', hex: '#16A34A' },
  { name: 'Purple', hex: '#9333EA' },
  { name: 'Silver', hex: '#94A3B8' },
];

const AdminAccessoriesTab = ({ adminFetch }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '', description: '', category: 'pens', base_price: '',
    has_colors: false, has_pack_sizes: false, discount_eligible: false,
    stock_quantity: '', max_per_order: '', in_stock: true, allow_preorder: false
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Color/Size editing
  const [editingColors, setEditingColors] = useState([]);
  const [editingSizes, setEditingSizes] = useState([]);
  const [newColor, setNewColor] = useState({ name: '', hex: '#000000', stock_quantity: 0, in_stock: true });
  const [newSize, setNewSize] = useState({ quantity: '', price: '', stock_quantity: 0, in_stock: true });
  const [modalColors, setModalColors] = useState([]);
  const [modalSizes, setModalSizes] = useState([]);
  const [newModalColor, setNewModalColor] = useState({ name: '', hex: '#000000', stock_quantity: 0, in_stock: true });
  const [newModalSize, setNewModalSize] = useState({ quantity: '', price: '', stock_quantity: 0, in_stock: true });

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await adminFetch(`${API_URL}/api/admin/shop/products`);
      if (res.ok) {
        const data = await res.json();
        setProducts(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingProduct(null);
    setFormData({ name: '', description: '', category: 'pens', base_price: '', has_colors: false, has_pack_sizes: false, discount_eligible: false, stock_quantity: '', max_per_order: '', in_stock: true, allow_preorder: false });
    setImageFile(null);
    setImagePreview(null);
    setModalColors([]);
    setModalSizes([]);
    setNewModalColor({ name: '', hex: '#000000', stock_quantity: 0, in_stock: true });
    setNewModalSize({ quantity: '', price: '', stock_quantity: 0, in_stock: true });
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || '',
      category: product.category,
      base_price: product.base_price || '',
      has_colors: product.has_colors || false,
      has_pack_sizes: product.has_pack_sizes || false,
      discount_eligible: product.discount_eligible || false,
      stock_quantity: product.stock_quantity ?? '',
      max_per_order: product.max_per_order ?? '',
      in_stock: product.in_stock !== false,
      allow_preorder: product.allow_preorder || false
    });
    setImageFile(null);
    setImagePreview(product.image_url ? `${API_URL}${product.image_url}` : null);
    setModalColors(product.colors || []);
    setModalSizes(product.pack_sizes || []);
    setNewModalColor({ name: '', hex: '#000000', stock_quantity: 0, in_stock: true });
    setNewModalSize({ quantity: '', price: '', stock_quantity: 0, in_stock: true });
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSaveProduct = async () => {
    if (!formData.name.trim()) { toast.error('Product name is required'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', formData.name);
      fd.append('description', formData.description);
      fd.append('category', formData.category);
      fd.append('base_price', formData.base_price || '0');
      fd.append('has_colors', formData.has_colors);
      fd.append('has_pack_sizes', formData.has_pack_sizes);
      fd.append('discount_eligible', formData.discount_eligible);
      fd.append('stock_quantity', formData.stock_quantity || '0');
      fd.append('max_per_order', formData.max_per_order || '0');
      fd.append('in_stock', formData.in_stock);
      fd.append('allow_preorder', formData.allow_preorder);
      if (imageFile) fd.append('image', imageFile);

      const url = editingProduct
        ? `${API_URL}/api/admin/shop/products/${editingProduct.product_id}`
        : `${API_URL}/api/admin/shop/products`;

      const token = localStorage.getItem('adminToken');
      const res = await fetch(url, {
        method: editingProduct ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });

      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || 'Failed to save');
      }

      const savedProduct = await res.json();
      const productId = savedProduct.product_id;

      // Save colors if has_colors is enabled and there are colors
      if (formData.has_colors && modalColors.length > 0) {
        await adminFetch(`${API_URL}/api/admin/shop/products/${productId}/colors`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ colors: modalColors })
        });
        
        // Upload any pending color images
        for (const color of modalColors) {
          if (color._pendingFile) {
            const cfd = new FormData();
            cfd.append('color_name', color.name);
            cfd.append('image', color._pendingFile);
            await fetch(`${API_URL}/api/admin/shop/products/${productId}/color-image`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` },
              body: cfd
            });
          }
        }
      }

      // Save pack sizes if has_pack_sizes is enabled and there are sizes
      if (formData.has_pack_sizes && modalSizes.length > 0) {
        await adminFetch(`${API_URL}/api/admin/shop/products/${productId}/pack-sizes`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pack_sizes: modalSizes })
        });
      }

      toast.success(editingProduct ? 'Product updated' : 'Product created');
      setShowModal(false);
      fetchProducts();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Delete this product permanently?')) return;
    try {
      const res = await adminFetch(`${API_URL}/api/admin/shop/products/${productId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('Product deleted');
      fetchProducts();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleNotifyCustomers = async (productId, productName) => {
    if (!window.confirm(`Send inventory notification email about "${productName}" to ALL customers?\n\nThis will email every registered customer about this product's availability.`)) return;
    try {
      const res = await adminFetch(`${API_URL}/api/admin/shop/products/${productId}/notify-customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_url: window.location.origin })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send notifications');
      let msg = `Sent to ${data.sent_count} customers`;
      if (data.failed_count > 0) msg += ` (${data.failed_count} failed)`;
      toast.success(msg);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleToggleVisibility = async (product) => {
    try {
      const token = localStorage.getItem('adminToken');
      const fd = new FormData();
      fd.append('visible', (!product.visible).toString());
      const res = await fetch(`${API_URL}/api/admin/shop/products/${product.product_id}`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}` }, body: fd
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(product.visible ? 'Product hidden' : 'Product visible');
      fetchProducts();
    } catch (e) { toast.error(e.message); }
  };

  // ========== COLOR MANAGEMENT ==========
  const openColorEditor = (product) => {
    setExpandedProduct(expandedProduct === product.product_id ? null : product.product_id);
    setEditingColors(product.colors || []);
    setNewColor({ name: '', hex: '#000000', stock_quantity: 0, in_stock: true });
  };

  const addColor = () => {
    if (!newColor.name.trim()) { toast.error('Color name required'); return; }
    if (editingColors.some(c => c.name.toLowerCase() === newColor.name.toLowerCase())) {
      toast.error('Color already exists'); return;
    }
    setEditingColors([...editingColors, { ...newColor, stock_quantity: parseInt(newColor.stock_quantity) || 0, image_url: '' }]);
    setNewColor({ name: '', hex: '#000000', stock_quantity: 0, in_stock: true });
  };

  const addPresetColor = (preset) => {
    if (editingColors.some(c => c.name.toLowerCase() === preset.name.toLowerCase())) return;
    setEditingColors([...editingColors, { ...preset, stock_quantity: 0, in_stock: true, image_url: '' }]);
  };

  const removeColor = (index) => {
    setEditingColors(editingColors.filter((_, i) => i !== index));
  };

  const updateColorStock = (index, field, value) => {
    setEditingColors(editingColors.map((c, i) =>
      i === index ? { ...c, [field]: field === 'stock_quantity' ? parseInt(value) || 0 : value } : c
    ));
  };

  const saveColors = async (productId) => {
    try {
      const res = await adminFetch(`${API_URL}/api/admin/shop/products/${productId}/colors`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ colors: editingColors })
      });
      if (!res.ok) throw new Error('Failed to save colors');
      toast.success('Colors saved');
      fetchProducts();
    } catch (e) { toast.error(e.message); }
  };

  const handleColorImageUpload = async (productId, colorName, file) => {
    if (!file) return;
    const token = localStorage.getItem('adminToken');
    const fd = new FormData();
    fd.append('color_name', colorName);
    fd.append('image', file);
    try {
      const res = await fetch(`${API_URL}/api/admin/shop/products/${productId}/color-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Upload failed'); }
      const data = await res.json();
      toast.success(`Image uploaded for ${colorName}`);
      // Update local state with new image_url
      setEditingColors(prev => prev.map(c =>
        c.name === colorName ? { ...c, image_url: data.image_url } : c
      ));
      fetchProducts();
    } catch (e) { toast.error(e.message); }
  };

  // ========== PACK SIZE MANAGEMENT ==========
  const openSizeEditor = (product) => {
    setExpandedProduct(expandedProduct === product.product_id ? null : product.product_id);
    setEditingSizes(product.pack_sizes || []);
    setNewSize({ quantity: '', price: '', stock_quantity: 0, in_stock: true });
  };

  const addPackSize = () => {
    if (!newSize.quantity || !newSize.price) { toast.error('Quantity and price required'); return; }
    const qty = parseInt(newSize.quantity);
    if (editingSizes.some(s => s.quantity === qty)) { toast.error('Pack size already exists'); return; }
    setEditingSizes([...editingSizes, { quantity: qty, price: parseFloat(newSize.price), stock_quantity: parseInt(newSize.stock_quantity) || 0, in_stock: newSize.in_stock }]);
    setNewSize({ quantity: '', price: '', stock_quantity: 0, in_stock: true });
  };

  const removePackSize = (index) => {
    setEditingSizes(editingSizes.filter((_, i) => i !== index));
  };

  const updateSizeField = (index, field, value) => {
    setEditingSizes(editingSizes.map((s, i) =>
      i === index ? { ...s, [field]: ['quantity', 'stock_quantity'].includes(field) ? parseInt(value) || 0 : field === 'price' ? parseFloat(value) || 0 : value } : s
    ));
  };

  const savePackSizes = async (productId) => {
    try {
      const res = await adminFetch(`${API_URL}/api/admin/shop/products/${productId}/pack-sizes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pack_sizes: editingSizes })
      });
      if (!res.ok) throw new Error('Failed to save');
      toast.success('Pack sizes saved');
      fetchProducts();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-6" data-testid="accessories-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Shop Products</h2>
          <p className="text-sm text-slate-500">Manage pens, cartridges, and accessories</p>
        </div>
        <Button onClick={openCreateModal} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="add-shop-product-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Product
        </Button>
      </div>

      {/* Product List */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading...</div>
      ) : products.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 text-lg font-medium">No shop products yet</p>
            <p className="text-sm text-slate-400 mt-1">Add pens, cartridges, or other accessories to get started</p>
            <Button onClick={openCreateModal} className="mt-4 bg-teal-600 hover:bg-teal-700 text-white">
              <Plus className="h-4 w-4 mr-2" /> Add First Product
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {products.map(product => (
            <Card key={product.product_id} className={`transition-all ${!product.visible ? 'opacity-50' : ''}`} data-testid={`shop-product-${product.product_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {/* Image */}
                  <div className="w-20 h-20 rounded-lg bg-slate-100 flex-shrink-0 overflow-hidden">
                    {product.image_url ? (
                      <img src={`${API_URL}${product.image_url}`} alt={product.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="h-8 w-8 text-slate-300" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-slate-900">{product.name}</h3>
                      <Badge className={
                        product.category === 'pens' ? 'bg-blue-100 text-blue-700' :
                        product.category === 'cartridges' ? 'bg-amber-100 text-amber-700' :
                        product.category === 'supplies' ? 'bg-green-100 text-green-700' :
                        'bg-slate-100 text-slate-700'
                      }>
                        {CATEGORIES.find(c => c.value === product.category)?.label || product.category}
                      </Badge>
                      {!product.discount_eligible && (
                        <Badge className="bg-red-50 text-red-600 text-xs" data-testid={`no-discount-badge-${product.product_id}`}>
                          No Discount
                        </Badge>
                      )}
                      {!product.visible && <Badge className="bg-slate-200 text-slate-600">Hidden</Badge>}
                    </div>
                    {product.description && (
                      <p className="text-sm text-slate-500 mt-1 line-clamp-1">{product.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                      {product.base_price > 0 && <span className="font-medium">${product.base_price.toFixed(2)}</span>}
                      {product.has_colors && (
                        <span className="flex items-center gap-1">
                          <Palette className="h-3.5 w-3.5" />
                          {product.colors?.length || 0} colors
                        </span>
                      )}
                      {product.has_pack_sizes && (
                        <span className="flex items-center gap-1">
                          <Package className="h-3.5 w-3.5" />
                          {product.pack_sizes?.length || 0} sizes
                        </span>
                      )}
                      {!product.has_colors && !product.has_pack_sizes && (
                        <>
                          <span className={`flex items-center gap-1 ${product.in_stock !== false ? 'text-green-600' : 'text-red-500'}`}>
                            <Package className="h-3.5 w-3.5" />
                            {product.in_stock !== false ? `In Stock (${product.stock_quantity || 0})` : 'Out of Stock'}
                          </span>
                          {product.max_per_order > 0 && (
                            <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                              Max {product.max_per_order}/order
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <button onClick={() => handleToggleVisibility(product)} className="p-2 rounded-lg hover:bg-slate-100 transition-colors" title={product.visible ? 'Hide' : 'Show'}>
                      {product.visible ? <Eye className="h-4 w-4 text-green-600" /> : <EyeOff className="h-4 w-4 text-slate-400" />}
                    </button>
                    {product.has_colors && (
                      <button onClick={() => openColorEditor(product)} className={`p-2 rounded-lg hover:bg-blue-50 transition-colors ${expandedProduct === product.product_id ? 'bg-blue-50 text-blue-600' : 'text-slate-400'}`} title="Manage Colors" data-testid={`manage-colors-${product.product_id}`}>
                        <Palette className="h-4 w-4" />
                      </button>
                    )}
                    {product.has_pack_sizes && (
                      <button onClick={() => openSizeEditor(product)} className={`p-2 rounded-lg hover:bg-amber-50 transition-colors ${expandedProduct === product.product_id ? 'bg-amber-50 text-amber-600' : 'text-slate-400'}`} title="Manage Pack Sizes" data-testid={`manage-sizes-${product.product_id}`}>
                        <Package className="h-4 w-4" />
                      </button>
                    )}
                    <button onClick={() => openEditModal(product)} className="p-2 rounded-lg hover:bg-teal-50 text-slate-400 hover:text-teal-600 transition-colors" title="Edit" data-testid={`edit-shop-${product.product_id}`}>
                      <Edit className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleNotifyCustomers(product.product_id, product.name)} className="p-2 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors" title="Notify all customers about this product" data-testid={`notify-shop-${product.product_id}`}>
                      <Mail className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(product.product_id)} className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors" title="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Expanded Color Editor */}
                {expandedProduct === product.product_id && product.has_colors && (
                  <div className="mt-4 pt-4 border-t border-slate-200" data-testid={`color-editor-${product.product_id}`}>
                    <h4 className="font-medium text-sm text-slate-700 mb-3 flex items-center gap-2">
                      <Palette className="h-4 w-4" /> Color Variants
                    </h4>
                    
                    {/* Preset colors */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {DEFAULT_COLORS.map(dc => (
                        <button key={dc.name} onClick={() => addPresetColor(dc)} className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs border transition-colors ${editingColors.some(c => c.name === dc.name) ? 'border-slate-300 bg-slate-100 opacity-50' : 'border-slate-200 hover:border-blue-300 hover:bg-blue-50'}`} disabled={editingColors.some(c => c.name === dc.name)}>
                          <span className="w-3 h-3 rounded-full border border-slate-300" style={{ backgroundColor: dc.hex }} />
                          {dc.name}
                        </button>
                      ))}
                    </div>

                    {/* Existing colors */}
                    <div className="space-y-2 mb-3">
                      {editingColors.map((color, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg flex-wrap">
                          <span className="w-6 h-6 rounded-full border-2 border-white shadow-sm flex-shrink-0" style={{ backgroundColor: color.hex }} />
                          <span className="font-medium text-sm text-slate-700 w-20">{color.name}</span>
                          
                          {/* Color image thumbnail + upload */}
                          <div className="flex items-center gap-1.5">
                            {color.image_url ? (
                              <img src={`${API_URL}${color.image_url}`} alt={color.name} className="w-8 h-8 rounded object-cover border border-slate-200" />
                            ) : (
                              <div className="w-8 h-8 rounded bg-slate-200 flex items-center justify-center border border-dashed border-slate-300">
                                <ImageIcon className="h-3.5 w-3.5 text-slate-400" />
                              </div>
                            )}
                            <input type="file" accept="image/*" className="hidden" id={`color-img-${expandedProduct}-${idx}`}
                              onChange={(e) => {
                                if (e.target.files[0]) handleColorImageUpload(expandedProduct, color.name, e.target.files[0]);
                              }} />
                            <label htmlFor={`color-img-${expandedProduct}-${idx}`} className="cursor-pointer p-1 hover:bg-slate-200 rounded text-slate-400 hover:text-slate-600 transition-colors" title="Upload color image">
                              <Upload className="h-3.5 w-3.5" />
                            </label>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-slate-500">Stock:</Label>
                            <Input type="number" value={color.stock_quantity} onChange={(e) => updateColorStock(idx, 'stock_quantity', e.target.value)} className="w-20 h-7 text-sm" min="0" />
                          </div>
                          <Switch checked={color.in_stock} onCheckedChange={(v) => updateColorStock(idx, 'in_stock', v)} />
                          <span className="text-xs text-slate-500">{color.in_stock ? 'In Stock' : 'Out'}</span>
                          <button onClick={() => removeColor(idx)} className="p-1 text-red-400 hover:text-red-600 ml-auto"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      ))}
                    </div>

                    {/* Add custom color */}
                    <div className="flex items-center gap-2 mb-3">
                      <input type="color" value={newColor.hex} onChange={(e) => setNewColor({ ...newColor, hex: e.target.value })} className="w-8 h-8 rounded cursor-pointer" />
                      <Input placeholder="Color name" value={newColor.name} onChange={(e) => setNewColor({ ...newColor, name: e.target.value })} className="h-8 text-sm w-32" />
                      <Input type="number" placeholder="Stock" value={newColor.stock_quantity} onChange={(e) => setNewColor({ ...newColor, stock_quantity: e.target.value })} className="h-8 text-sm w-20" min="0" />
                      <Button size="sm" variant="outline" onClick={addColor} className="h-8"><Plus className="h-3.5 w-3.5 mr-1" />Add</Button>
                    </div>

                    <Button onClick={() => saveColors(product.product_id)} className="bg-blue-600 hover:bg-blue-700 text-white" size="sm" data-testid={`save-colors-${product.product_id}`}>
                      <Save className="h-4 w-4 mr-1" /> Save Colors
                    </Button>
                  </div>
                )}

                {/* Expanded Pack Size Editor */}
                {expandedProduct === product.product_id && product.has_pack_sizes && (
                  <div className="mt-4 pt-4 border-t border-slate-200" data-testid={`size-editor-${product.product_id}`}>
                    <h4 className="font-medium text-sm text-slate-700 mb-3 flex items-center gap-2">
                      <Package className="h-4 w-4" /> Pack Sizes
                    </h4>

                    <div className="space-y-2 mb-3">
                      {editingSizes.map((size, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                          <Badge className="bg-amber-100 text-amber-700">{size.quantity}-pack</Badge>
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-slate-500">Price: $</Label>
                            <Input type="number" value={size.price} onChange={(e) => updateSizeField(idx, 'price', e.target.value)} className="w-24 h-7 text-sm" min="0" step="0.01" />
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-slate-500">Stock:</Label>
                            <Input type="number" value={size.stock_quantity} onChange={(e) => updateSizeField(idx, 'stock_quantity', e.target.value)} className="w-20 h-7 text-sm" min="0" />
                          </div>
                          <Switch checked={size.in_stock} onCheckedChange={(v) => updateSizeField(idx, 'in_stock', v)} />
                          <span className="text-xs text-slate-500">{size.in_stock ? 'In Stock' : 'Out'}</span>
                          <button onClick={() => removePackSize(idx)} className="p-1 text-red-400 hover:text-red-600 ml-auto"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      ))}
                    </div>

                    {/* Add pack size */}
                    <div className="flex items-center gap-2 mb-3">
                      <Input type="number" placeholder="Qty" value={newSize.quantity} onChange={(e) => setNewSize({ ...newSize, quantity: e.target.value })} className="h-8 text-sm w-20" min="1" />
                      <Input type="number" placeholder="Price" value={newSize.price} onChange={(e) => setNewSize({ ...newSize, price: e.target.value })} className="h-8 text-sm w-24" min="0" step="0.01" />
                      <Input type="number" placeholder="Stock" value={newSize.stock_quantity} onChange={(e) => setNewSize({ ...newSize, stock_quantity: e.target.value })} className="h-8 text-sm w-20" min="0" />
                      <Button size="sm" variant="outline" onClick={addPackSize} className="h-8"><Plus className="h-3.5 w-3.5 mr-1" />Add</Button>
                    </div>

                    <Button onClick={() => savePackSizes(product.product_id)} className="bg-amber-600 hover:bg-amber-700 text-white" size="sm" data-testid={`save-sizes-${product.product_id}`}>
                      <Save className="h-4 w-4 mr-1" /> Save Pack Sizes
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="font-bold text-lg text-slate-900">{editingProduct ? 'Edit Product' : 'Add Shop Product'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-lg"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Image */}
              <div>
                <Label className="text-sm font-medium text-slate-700">Product Image</Label>
                <div className="mt-1 flex items-center gap-4">
                  <div className="w-24 h-24 rounded-lg bg-slate-100 overflow-hidden flex items-center justify-center border-2 border-dashed border-slate-300">
                    {imagePreview ? (
                      <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <Upload className="h-8 w-8 text-slate-300" />
                    )}
                  </div>
                  <div>
                    <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" id="shop-image-upload" />
                    <label htmlFor="shop-image-upload" className="cursor-pointer inline-flex items-center px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium text-slate-700 transition-colors">
                      <Upload className="h-4 w-4 mr-2" /> Upload Image
                    </label>
                  </div>
                </div>
              </div>

              {/* Name */}
              <div>
                <Label className="text-sm font-medium text-slate-700">Product Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Insulin Pen Device" className="mt-1" data-testid="shop-product-name" />
              </div>

              {/* Category */}
              <div>
                <Label className="text-sm font-medium text-slate-700">Category</Label>
                <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="mt-1 w-full h-9 rounded-md border border-slate-200 px-3 text-sm" data-testid="shop-product-category">
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>

              {/* Description */}
              <div>
                <Label className="text-sm font-medium text-slate-700">Description</Label>
                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Product description..." className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm min-h-[80px] focus:outline-none focus:ring-1 focus:ring-teal-500" />
              </div>

              {/* Base Price */}
              <div>
                <Label className="text-sm font-medium text-slate-700">Base Price ($)</Label>
                <Input type="number" value={formData.base_price} onChange={(e) => setFormData({ ...formData, base_price: e.target.value })} placeholder="0.00" min="0" step="0.01" className="mt-1" data-testid="shop-product-price" />
                <p className="text-xs text-slate-400 mt-1">For products with pack sizes, set individual prices per size instead.</p>
              </div>

              {/* Inventory (for simple products without colors/pack sizes) */}
              {!formData.has_colors && !formData.has_pack_sizes && (
                <div className="p-4 bg-green-50/50 rounded-lg border border-green-100 space-y-3">
                  <h4 className="font-medium text-sm text-slate-700 flex items-center gap-2">
                    <Package className="h-4 w-4 text-green-600" /> Inventory
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-600">Stock Quantity</Label>
                      <Input type="number" value={formData.stock_quantity} onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })} placeholder="0" min="0" className="mt-1 h-9" data-testid="shop-stock-quantity" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">Max Per Order</Label>
                      <Input type="number" value={formData.max_per_order} onChange={(e) => setFormData({ ...formData, max_per_order: e.target.value })} placeholder="0 = unlimited" min="0" className="mt-1 h-9" data-testid="shop-max-per-order" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-slate-500">Mark as in stock?</p>
                    <Switch checked={formData.in_stock} onCheckedChange={(v) => setFormData({ ...formData, in_stock: v })} data-testid="shop-in-stock-toggle" />
                  </div>
                </div>
              )}

              {/* Toggles */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Has Color Variants</p>
                    <p className="text-xs text-slate-500">Enable for pens/devices with multiple colors</p>
                  </div>
                  <Switch checked={formData.has_colors} onCheckedChange={(v) => setFormData({ ...formData, has_colors: v })} data-testid="toggle-has-colors" />
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Has Pack Sizes</p>
                    <p className="text-xs text-slate-500">Enable for cartridges/supplies with different quantities</p>
                  </div>
                  <Switch checked={formData.has_pack_sizes} onCheckedChange={(v) => setFormData({ ...formData, has_pack_sizes: v })} data-testid="toggle-has-pack-sizes" />
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Eligible for Discounts</p>
                    <p className="text-xs text-slate-500">Allow discount codes & tier pricing for this product</p>
                  </div>
                  <Switch checked={formData.discount_eligible} onCheckedChange={(v) => setFormData({ ...formData, discount_eligible: v })} data-testid="toggle-discount-eligible" />
                </div>
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Allow Pre-Orders</p>
                    <p className="text-xs text-slate-500">When ON, customers can still order this item while it's out of stock (shown as a "Pre-Order" button). When OFF, out-of-stock blocks the sale.</p>
                  </div>
                  <Switch checked={formData.allow_preorder} onCheckedChange={(v) => setFormData({ ...formData, allow_preorder: v })} data-testid="shop-allow-preorder-toggle" />
                </div>
              </div>

              {/* Inline Color Management (inside modal) */}
              {formData.has_colors && (
                <div className="mt-2 p-4 bg-blue-50/50 rounded-lg border border-blue-100">
                  <h4 className="font-medium text-sm text-slate-700 mb-3 flex items-center gap-2">
                    <Palette className="h-4 w-4 text-blue-600" /> Color Variants
                  </h4>

                  {/* Quick-add preset colors */}
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {DEFAULT_COLORS.map(dc => (
                      <button key={dc.name} type="button" onClick={() => {
                        if (modalColors.some(c => c.name.toLowerCase() === dc.name.toLowerCase())) return;
                        setModalColors([...modalColors, { ...dc, stock_quantity: 0, in_stock: true, image_url: '' }]);
                      }} className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs border transition-colors ${modalColors.some(c => c.name === dc.name) ? 'border-slate-300 bg-slate-100 opacity-50' : 'border-slate-200 hover:border-blue-300 hover:bg-blue-50'}`} disabled={modalColors.some(c => c.name === dc.name)}>
                        <span className="w-3 h-3 rounded-full border border-slate-300" style={{ backgroundColor: dc.hex }} />
                        {dc.name}
                      </button>
                    ))}
                  </div>

                  {/* Color list */}
                  {modalColors.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {modalColors.map((color, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-white rounded-lg border border-slate-200 flex-wrap">
                          <span className="w-6 h-6 rounded-full border-2 border-white shadow-sm flex-shrink-0" style={{ backgroundColor: color.hex }} />
                          <span className="font-medium text-sm text-slate-700 min-w-[60px]">{color.name}</span>
                          
                          {/* Color image */}
                          <div className="flex items-center gap-1">
                            {(color.image_url || color._previewUrl) ? (
                              <img src={color._previewUrl || `${API_URL}${color.image_url}`} alt={color.name} className="w-8 h-8 rounded object-cover border border-slate-200" />
                            ) : (
                              <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center border border-dashed border-slate-300">
                                <ImageIcon className="h-3.5 w-3.5 text-slate-400" />
                              </div>
                            )}
                            <input type="file" accept="image/*" className="hidden" id={`modal-color-img-${idx}`}
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (!file) return;
                                setModalColors(prev => prev.map((c, i) =>
                                  i === idx ? { ...c, _pendingFile: file, _previewUrl: URL.createObjectURL(file) } : c
                                ));
                              }} />
                            <label htmlFor={`modal-color-img-${idx}`} className="cursor-pointer p-1 hover:bg-blue-100 rounded text-blue-500 hover:text-blue-700 transition-colors" title="Upload image for this color">
                              <Upload className="h-3.5 w-3.5" />
                            </label>
                          </div>

                          <div className="flex items-center gap-1">
                            <Label className="text-xs text-slate-500">Stock:</Label>
                            <Input type="number" value={color.stock_quantity} onChange={(e) => setModalColors(prev => prev.map((c, i) => i === idx ? { ...c, stock_quantity: parseInt(e.target.value) || 0 } : c))} className="w-16 h-7 text-sm" min="0" />
                          </div>
                          <Switch checked={color.in_stock} onCheckedChange={(v) => setModalColors(prev => prev.map((c, i) => i === idx ? { ...c, in_stock: v } : c))} />
                          <span className="text-xs text-slate-500 w-12">{color.in_stock ? 'In Stock' : 'Out'}</span>
                          <button type="button" onClick={() => setModalColors(prev => prev.filter((_, i) => i !== idx))} className="p-1 text-red-400 hover:text-red-600 ml-auto">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add custom color */}
                  <div className="flex items-center gap-2">
                    <input type="color" value={newModalColor.hex} onChange={(e) => setNewModalColor({ ...newModalColor, hex: e.target.value })} className="w-8 h-8 rounded cursor-pointer" />
                    <Input placeholder="Color name" value={newModalColor.name} onChange={(e) => setNewModalColor({ ...newModalColor, name: e.target.value })} className="h-8 text-sm flex-1" />
                    <Input type="number" placeholder="Stock" value={newModalColor.stock_quantity} onChange={(e) => setNewModalColor({ ...newModalColor, stock_quantity: e.target.value })} className="h-8 text-sm w-16" min="0" />
                    <Button size="sm" variant="outline" type="button" onClick={() => {
                      if (!newModalColor.name.trim()) { toast.error('Color name required'); return; }
                      if (modalColors.some(c => c.name.toLowerCase() === newModalColor.name.toLowerCase())) { toast.error('Color exists'); return; }
                      setModalColors([...modalColors, { ...newModalColor, stock_quantity: parseInt(newModalColor.stock_quantity) || 0, image_url: '' }]);
                      setNewModalColor({ name: '', hex: '#000000', stock_quantity: 0, in_stock: true });
                    }} className="h-8"><Plus className="h-3.5 w-3.5 mr-1" />Add</Button>
                  </div>

                  {modalColors.length === 0 && (
                    <p className="text-xs text-slate-400 mt-2">Click a preset above or add a custom color to get started.</p>
                  )}
                </div>
              )}

              {/* Inline Pack Size Management (inside modal) */}
              {formData.has_pack_sizes && (
                <div className="mt-2 p-4 bg-amber-50/50 rounded-lg border border-amber-100">
                  <h4 className="font-medium text-sm text-slate-700 mb-3 flex items-center gap-2">
                    <Package className="h-4 w-4 text-amber-600" /> Pack Sizes
                  </h4>

                  {modalSizes.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {modalSizes.map((size, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-white rounded-lg border border-slate-200">
                          <Badge className="bg-amber-100 text-amber-700">{size.quantity}-pack</Badge>
                          <div className="flex items-center gap-1">
                            <Label className="text-xs text-slate-500">$</Label>
                            <Input type="number" value={size.price} onChange={(e) => setModalSizes(prev => prev.map((s, i) => i === idx ? { ...s, price: parseFloat(e.target.value) || 0 } : s))} className="w-20 h-7 text-sm" min="0" step="0.01" />
                          </div>
                          <div className="flex items-center gap-1">
                            <Label className="text-xs text-slate-500">Stock:</Label>
                            <Input type="number" value={size.stock_quantity} onChange={(e) => setModalSizes(prev => prev.map((s, i) => i === idx ? { ...s, stock_quantity: parseInt(e.target.value) || 0 } : s))} className="w-16 h-7 text-sm" min="0" />
                          </div>
                          <Switch checked={size.in_stock} onCheckedChange={(v) => setModalSizes(prev => prev.map((s, i) => i === idx ? { ...s, in_stock: v } : s))} />
                          <span className="text-xs text-slate-500 w-12">{size.in_stock ? 'In Stock' : 'Out'}</span>
                          <button type="button" onClick={() => setModalSizes(prev => prev.filter((_, i) => i !== idx))} className="p-1 text-red-400 hover:text-red-600 ml-auto">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <Input type="number" placeholder="Qty" value={newModalSize.quantity} onChange={(e) => setNewModalSize({ ...newModalSize, quantity: e.target.value })} className="h-8 text-sm w-16" min="1" />
                    <Input type="number" placeholder="Price" value={newModalSize.price} onChange={(e) => setNewModalSize({ ...newModalSize, price: e.target.value })} className="h-8 text-sm w-20" min="0" step="0.01" />
                    <Input type="number" placeholder="Stock" value={newModalSize.stock_quantity} onChange={(e) => setNewModalSize({ ...newModalSize, stock_quantity: e.target.value })} className="h-8 text-sm w-16" min="0" />
                    <Button size="sm" variant="outline" type="button" onClick={() => {
                      if (!newModalSize.quantity || !newModalSize.price) { toast.error('Qty and price required'); return; }
                      const qty = parseInt(newModalSize.quantity);
                      if (modalSizes.some(s => s.quantity === qty)) { toast.error('Size exists'); return; }
                      setModalSizes([...modalSizes, { quantity: qty, price: parseFloat(newModalSize.price), stock_quantity: parseInt(newModalSize.stock_quantity) || 0, in_stock: true }]);
                      setNewModalSize({ quantity: '', price: '', stock_quantity: 0, in_stock: true });
                    }} className="h-8"><Plus className="h-3.5 w-3.5 mr-1" />Add</Button>
                  </div>

                  {modalSizes.length === 0 && (
                    <p className="text-xs text-slate-400 mt-2">Add pack sizes (e.g., 5-pack, 10-pack, 15-pack).</p>
                  )}
                </div>
              )}
            </div>

            <div className="p-5 border-t border-slate-200 flex gap-3">
              <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleSaveProduct} disabled={saving} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-shop-product-btn">
                {saving ? 'Saving...' : editingProduct ? 'Update Product' : 'Create Product'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAccessoriesTab;
